import * as React from 'react'
import * as styles from './search.bar.styl'
import Icon from '../icon'
import cx from 'classnames'
import { CSSSelector } from '@src/utils/css.selector.helper'
import { debounce, DebounceSettings } from 'lodash'

export interface Props {
  id?: string
  role?: string
  autoComplete?: string
  className?: string
  inputClassName?: string
  searchValue: string
  placeholder?: string
  disabled?: boolean
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  onKeyPress?: (
    event: React.KeyboardEvent<HTMLInputElement> &
      React.InvalidEvent<HTMLInputElement>
  ) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onClear?: () => void
  style?: React.CSSProperties
  isClearable?: boolean
  debounced?: boolean
  debounceWait?: number
  debouncedSettings?: DebounceSettings
}

type SearchBarComponent<T> = React.ForwardRefExoticComponent<
  T & React.RefAttributes<HTMLInputElement>
> &
  React.FC<T> & {
    ref?: React.Ref<HTMLInputElement>
    CSSSelectors?: {
      searchInput: CSSSelector
    }
  }

const SearchBar: SearchBarComponent<Props> = React.forwardRef(
  (props: Props, ref: React.Ref<HTMLInputElement>) => {
    const {
      id,
      inputClassName,
      disabled,
      autoComplete,
      onFocus,
      onKeyPress,
      onKeyDown,
      onChange,
      onClear,
      searchValue,
      placeholder,
      style,
      className,
      role,
      debounced,
      debounceWait = 300,
      debouncedSettings,
      isClearable
    } = props

    const [value, setValue] = React.useState(searchValue)

    function handleChangeValue(e: React.ChangeEvent<HTMLInputElement>) {
      if (typeof onChange === 'function') {
        onChange(e)
      }
    }

    function handleKeyPress(
      e: React.KeyboardEvent<HTMLInputElement> &
        React.InvalidEvent<HTMLInputElement>
    ) {
      if (typeof onKeyPress === 'function') {
        onKeyPress(e)
      }
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
      if (typeof onKeyDown === 'function') {
        onKeyDown(e)
      }
    }

    function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
      if (typeof onFocus === 'function') {
        onFocus(e)
      }
    }

    const debouncedChangeHandler = React.useMemo(
      () => debounce(handleChangeValue, debounceWait, debouncedSettings),
      []
    )

    React.useEffect(() => {
      setValue(searchValue)
    }, [searchValue])

    React.useEffect(
      () => () => {
        debounced && debouncedChangeHandler?.cancel()
      },
      []
    )

    function handler(e: React.ChangeEvent<HTMLInputElement>) {
      // Prevents React from resetting its properties:
      e?.persist()
      setValue(e?.currentTarget?.value)
      return debounced ? debouncedChangeHandler(e) : handleChangeValue(e)
    }

    function onClearValue() {
      setValue('')
      if (typeof onChange === 'function') {
        onClear()
      }
    }

    return (
      <div
        data-role={role}
        className={cx('searchBar', className, styles.searchWrapper)}
        style={style}
      >
        <div className={styles.icon} />
        <input
          ref={ref}
          id={id}
          type="text"
          autoComplete={autoComplete}
          placeholder={placeholder}
          value={value}
          className={cx(
            styles.search,
            SearchBar.CSSSelectors.searchInput.value,
            inputClassName
          )}
          onChange={handler}
          onKeyPress={handleKeyPress}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        {isClearable && (
          <span role={'clear-search'} onClick={onClearValue}>
            <Icon className={styles.clearable} name="times-circle" />
          </span>
        )}
      </div>
    )
  }
)

SearchBar.defaultProps = {
  placeholder: 'Object Search'
}

SearchBar.CSSSelectors = {
  searchInput: new CSSSelector('searchInput')
}

export default SearchBar
